import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getGoogleLoginUrl, getMicrosoftLoginUrl, getAppleLoginUrl, register, resendVerification } from '../services';
import './Login.css';
import { safeReturnTo } from '../utils/returnTo';
import { markAuthHandoff } from '../utils/authHandoff';

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  // The server reports the send's actual outcome now. False means the account
  // exists but the email never left — the check-email screen must lead with
  // resend, not point at an inbox that will stay empty.
  const [emailSent, setEmailSent] = useState(true);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const errorRef = useRef<HTMLDivElement>(null);

  // The error banner renders above a long form — after submitting from the
  // bottom, it's off-screen unless we bring it into view.
  useEffect(() => {
    if (error) errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [error]);

  const handleGoogleLogin = () => {
    window.location.href = getGoogleLoginUrl();
  };

  const handleMicrosoftLogin = () => {
    window.location.href = getMicrosoftLoginUrl();
  };

  const handleAppleLogin = () => {
    window.location.href = getAppleLoginUrl();
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await register({
        email,
        password,
        first_name: firstName,
        last_name: lastName,
      });
      // Show verification message instead of redirecting
      if (response.requires_verification) {
        // Missing field (older server) reads as sent — the flag only ever
        // downgrades the claim.
        setEmailSent(response.email_sent !== false);
        setVerificationSent(true);
      } else {
        markAuthHandoff();
        window.location.href = safeReturnTo();
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (verificationSent) {
    const emailFailed = !emailSent && resendState !== 'sent';
    const handleResend = async () => {
      if (resendState === 'sending' || resendState === 'sent') return;
      setResendState('sending');
      try {
        const result = await resendVerification(email);
        setResendState(result.email_sent === false ? 'failed' : 'sent');
      } catch {
        setResendState('failed');
      }
    };
    return (
      <div className="login-page">
        <div className="login-container">
          <h1 className="login-title">{emailFailed ? 'One More Step' : 'Check Your Email'}</h1>
          {emailFailed ? (
            <div className="login-error">
              Your account is ready, but we couldn't send the verification email
              to <strong>{email}</strong>. Resend it below.
            </div>
          ) : (
            <div className="login-success">
              We've sent a verification link to <strong>{email}</strong>
            </div>
          )}
          <p className="login-subtitle">
            {emailFailed
              ? 'Once the email arrives, click its link to verify your account.'
              : 'Click the link in the email to verify your account and start using VizDay.'}
          </p>
          <button
            type="button"
            className="login-button email-login"
            onClick={handleResend}
            disabled={resendState === 'sending' || resendState === 'sent'}
          >
            {resendState === 'sent' ? 'Email sent — check your inbox'
              : resendState === 'sending' ? 'Sending…'
                : emailFailed ? 'Resend verification email' : "Didn't get it? Resend email"}
          </button>
          {resendState === 'failed' && (
            <p className="login-subtitle">
              Still couldn't send it. Check the address and try again in a minute.
            </p>
          )}
          <Link to="/login" className="login-button email-login" style={{ textDecoration: 'none' }}>
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <h1 className="login-title">Join VizDay</h1>
        <p className="login-subtitle">Your Daily Command Center</p>

        {error && <div className="login-error" ref={errorRef}>{error}</div>}

        {/* OAuth Buttons */}
        <div className="oauth-buttons">
          <button className="login-button google-login" onClick={handleGoogleLogin}>
            <svg className="oauth-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <button className="login-button microsoft-login" onClick={handleMicrosoftLogin}>
            <svg className="oauth-icon" viewBox="0 0 21 21">
              <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
              <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
            </svg>
            Continue with Microsoft
          </button>

          <button className="login-button apple-login" onClick={handleAppleLogin}>
            <svg className="oauth-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
            Continue with Apple
          </button>
        </div>

        <div className="login-divider">
          <span>or</span>
        </div>

        {/* Email/Password Form */}
        <form onSubmit={handleSignup} className="login-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName">First name</label>
              <input
                type="text"
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName">Last name</label>
              <input
                type="text"
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Enter password again"
              required
            />
          </div>

          <button type="submit" className="login-button email-login" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="login-footer">
          <span>Already have an account? </span>
          <Link to="/login" className="signup-link">Sign in</Link>
        </div>

        <div className="login-footer login-legal">
          By signing up, you agree to our{' '}
          <Link to="/terms">Terms</Link>
          {' and '}
          <Link to="/privacy">Privacy Policy</Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
